import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import { useData } from '../../../contexts/DataContext';
import useFormSecurity from '../../../hooks/useFormSecurity';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../../common/SafeIcon';

const { FiBriefcase, FiLock, FiMail, FiArrowRight, FiUserPlus, FiInfo, FiArrowLeft } = FiIcons;

const PartnerLogin = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const { login } = useAuth();
  const { authenticateUser } = useData();
  const { validateSubmission, HoneypotField } = useFormSecurity('partner_login');
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateSubmission()) return;

    setError('');
    setLoading(true);

    try {
      // Sjekk autentisering mot database
      let user = await authenticateUser(email, password);

      // Sørg for at demo-brukeren alltid fungerer selv om den ikke ligger i databasen
      if (!user && email.toLowerCase() === 'partner@eksempel.no' && password === 'Partner123!') {
        user = {
          id: 'demo_partner_id',
          partnerId: 'PARTNER-DEMO',
          partner_id: 'PARTNER-DEMO',
          name: 'Demo Partner AS',
          email: 'partner@eksempel.no',
          role: 'partner',
          active: true
        };
      }

      if (user && (user.role === 'partner' || user.role === 'superadmin')) {
        const success = await login(user, 'partner');
        if (success) {
          navigate('/partner/dashboard');
        } else {
          setError('Kunne ikke opprette sesjon.');
        }
      } else {
        setError('Ugyldig e-post eller passord for partner.');
      }
    } catch (err) {
      console.error(err);
      setError('Det oppstod en teknisk feil ved innlogging.');
    } finally {
      setLoading(false);
    }
  };

  const fillDemo = () => {
    setEmail('partner@eksempel.no');
    setPassword('Partner123!');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center px-4">
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full"
      >
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <SafeIcon icon={FiBriefcase} className="text-blue-600 text-2xl" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900">Partnerportal</h1>
          <p className="text-gray-500 mt-2">Logg inn for å administrere dine kunder</p>
        </div>

        {error && (
          <motion.div 
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            className="bg-red-50 text-red-600 p-4 rounded-lg mb-6 text-sm flex items-start gap-2 border border-red-100"
          >
            <SafeIcon icon={FiInfo} className="mt-0.5" />
            <span>{error}</span>
          </motion.div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          <HoneypotField />
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">E-post</label>
            <div className="relative">
              <SafeIcon icon={FiMail} className="absolute left-3 top-3 text-gray-400" />
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all"
                placeholder="partner@eksempel.no"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Passord</label>
            <div className="relative">
              <SafeIcon icon={FiLock} className="absolute left-3 top-3 text-gray-400" />
              <input
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all"
                placeholder="••••••••"
              />
            </div>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-blue-600 text-white py-3 rounded-lg hover:bg-blue-700 transition-colors font-medium flex items-center justify-center gap-2 shadow-sm disabled:opacity-70"
          >
            {loading ? (
              <>
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                <span>Logger inn...</span>
              </>
            ) : (
              <>
                Logg inn <SafeIcon icon={FiArrowRight} />
              </>
            )}
          </button>
        </form>

        <div 
          onClick={fillDemo}
          className="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-100 text-sm cursor-pointer hover:bg-blue-100 transition-colors group"
          title="Klikk for å fylle ut automatisk"
        >
          <div className="flex items-center justify-between mb-2">
            <p className="font-semibold text-blue-900">Demo Partner:</p>
            <span className="text-xs bg-blue-200 text-blue-800 px-2 py-0.5 rounded opacity-0 group-hover:opacity-100 transition-opacity">Klikk for å fylle ut</span>
          </div>
          <div className="grid grid-cols-[60px_1fr] gap-1 text-blue-800">
            <span>E-post:</span>
            <span className="font-mono">partner@eksempel.no</span>
            <span>Passord:</span>
            <span className="font-mono">Partner123!</span>
          </div>
        </div>

        <div className="mt-8 pt-6 border-t border-gray-100">
          <button
            onClick={() => navigate('/partner/register')}
            className="w-full bg-white border-2 border-gray-200 text-gray-700 py-2.5 rounded-lg hover:border-blue-300 hover:text-blue-600 transition-all font-medium flex items-center justify-center gap-2 mb-4 text-sm"
          >
            <SafeIcon icon={FiUserPlus} /> Registrer ny partner
          </button>
          
          <button
            onClick={() => navigate('/')}
            className="w-full text-gray-400 hover:text-gray-600 text-sm font-medium transition-colors flex items-center justify-center gap-1"
          >
            <SafeIcon icon={FiArrowLeft} /> Tilbake til forsiden
          </button>
        </div>

      </motion.div>
    </div>
  );
};

export default PartnerLogin;